import { shouldProcessFrame } from '../utils/frameScheduler.js';

/**
 * BaseExerciseStrategy - Abstract base class for exercise detection strategies
 *
 * Each exercise type (skipping, pickleball, etc.) implements a strategy that handles:
 * - Detection loop (how to detect reps/strokes)
 * - Canvas drawing (how to visualize detections)
 * - Status display (exercise-specific status indicators)
 * - Debug panel (exercise-specific debug info)
 * - Labels and configuration
 */
export class BaseExerciseStrategy {
  /**
   * Unique identifier for this strategy
   * @returns {string}
   */
  static get id() {
    throw new Error('Strategy must implement static id getter');
  }

  /**
   * Human-readable display name
   * @returns {string}
   */
  static get displayName() {
    throw new Error('Strategy must implement static displayName getter');
  }

  /**
   * Detector type to use ('POSE' or 'YOLO')
   * @returns {string}
   */
  static get detectorType() {
    throw new Error('Strategy must implement static detectorType getter');
  }

  /**
   * Counter ID to use (from counterRegistry)
   * @returns {string}
   */
  static get counterId() {
    throw new Error('Strategy must implement static counterId getter');
  }

  /**
   * Audio preset key (from AUDIO_PRESETS)
   * @returns {string}
   */
  static get audioPreset() {
    return 'skipping'; // default
  }

  /**
   * Get labels for this exercise type
   * @returns {Object} Labels object
   */
  getLabels() {
    return {
      title: 'Exercise Counter',
      repNoun: 'Reps',
      cameraTip: 'Position camera properly',
      beepMessage: 'Every time I did it correctly',
      loadingModel: 'Loading detection model...'
    };
  }

  /**
   * Check if all additional models required by this strategy are ready
   * Override in subclasses that load additional models (e.g., pose detector)
   * @returns {boolean} True if all additional models are ready
   */
  isAdditionalModelsReady() {
    return true; // Base implementation assumes no additional models
  }

  /**
   * Initialize any additional models required by this strategy
   * Override in subclasses that need to load additional models
   * @param {string} railsEnv - Rails environment ('development', 'test', 'production')
   * @returns {Promise<void>}
   */
  async initializeAdditionalModels(railsEnv = 'development') {
    // Base implementation does nothing
  }

  /**
   * Initialize the strategy with refs and callbacks
   * @param {Object} params - Initialization parameters
   * @param {Object} params.videoRef - React ref to video element
   * @param {Object} params.canvasRef - React ref to canvas element
   * @param {Object} params.counterRef - React ref to counter instance
   * @param {Function} params.detect - Detection function from useDetector
   * @param {Function} params.updateCounter - Counter update function
   * @param {Function} params.getCounterState - Get counter state function
   * @param {Function} params.syncCanvasSize - Sync canvas to video size
   * @param {Function} params.clearCanvas - Clear the canvas
   */
  initialize(params) {
    this.videoRef = params.videoRef;
    this.canvasRef = params.canvasRef;
    this.counterRef = params.counterRef;
    this.detect = params.detect;
    this.updateCounter = params.updateCounter;
    this.getCounterState = params.getCounterState;
    this.syncCanvasSize = params.syncCanvasSize;
    this.clearCanvas = params.clearCanvas;

    // FPS tracking state
    this.lastFpsCheckTime = null;
    this.lastFpsCheckFrame = 0;
    this.currentFps = null;

    // Engine display state
    this.currentEngine = null;

    // Inference timing
    this._lastInferenceMs = null;
  }

  /**
   * Set the current inference engine for display on canvas
   * @param {string} engine - Engine identifier (e.g., 'tfjs-webgl', 'tfjs-webgpu')
   */
  setCurrentEngine(engine) {
    this.currentEngine = engine;
  }

  /**
   * Human-readable label for the current inference engine, e.g.
   *   'tfjs-webgl'   -> 'TFJS WebGL'
   *   'tfjs-webgpu'  -> 'TFJS WebGPU'
   *   'onnx-webgpu'  -> 'ONNX WebGPU'
   * Falls back to the raw string or '?' when nothing is set.
   */
  engineDisplayLabel() {
    if (!this.currentEngine) return "?";
    const [runtime, backend] = this.currentEngine.split("-");
    const runtimeLabel = runtime === "onnx" ? "ONNX" : runtime === "tfjs" ? "TFJS" : runtime;
    const backendLabel = backend === "webgpu" ? "WebGPU" : backend === "webgl" ? "WebGL" : backend;
    return backendLabel ? `${runtimeLabel} ${backendLabel}` : runtimeLabel;
  }

  /**
   * Start the detection loop - Template Method Pattern
   *
   * This method implements the common flow for all strategies:
   * detect → convert → store to movementLog → counter reads from log → draw → callbacks
   *
   * Subclasses must implement:
   * - createDetectionLoopMechanism(runFn) - returns cleanup function (RAF or setInterval)
   * - runDetection(video) - returns raw detections
   * - buildFrameData(rawDetections, timestamp) - converts to frame data format
   * - drawFrame(ctx, video, rawDetections, frameData, lastRepCount) - draws on canvas
   * - getStatusFromFrameData(frameData) - extracts status object
   * - computeTimestamp(frameCounterRef, sessionStartTimeRef) - computes frame timestamp
   *
   * @param {Object} params - Loop parameters
   * @param {Function} params.onRepDetected - Callback when rep is detected
   * @param {Function} params.onStatusUpdate - Callback to update detection status
   * @param {Function} params.onDebugUpdate - Callback to update debug info
   * @param {Function} params.onFrameRecorded - Callback when frame is recorded
   * @param {Object} params.isActiveRef - Ref to check if still active
   * @param {Object} params.sessionStartTimeRef - Ref to session start time
   * @param {Object} params.frameCounterRef - Ref to frame counter
   * @param {Object} params.lastRepCountRef - Ref to last rep count
   * @param {Object} params.movementLogRef - Ref to movement log array
   * @param {Object} params.recordedVideoDimensionsRef - Ref to store video dimensions for saving
   * @param {boolean} params.maxFPS - If true, run at maximum speed without frame delay (for benchmarking)
   * @returns {Function} Cleanup function to stop the loop
   */
  startDetectionLoop(params) {
    const {
      onRepDetected,
      onStatusUpdate,
      onDebugUpdate,
      onFrameRecorded,
      isActiveRef,
      sessionStartTimeRef,
      frameCounterRef,
      lastRepCountRef,
      movementLogRef,
      recordedVideoDimensionsRef,
      maxFPS = false
    } = params;

    // Strategy-owned cumulative count for detecting new reps. Local to this
    // loop instance so it can't collide with lastRepCountRef, which the UI
    // writes to with the displayed count (= initialReps + new reps) and would
    // otherwise gate this check away after the first rep on a resumed session.
    let strategyCounterTotal = 0;

    const runDetectionCycle = async () => {
      if (!isActiveRef.current || !this.videoRef.current || !this.canvasRef.current) {
        return;
      }

      const video = this.videoRef.current;
      const canvas = this.canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Capture video dimensions on first frame if not already captured
      // This is critical for correct coordinate normalization in recordings
      if (recordedVideoDimensionsRef && !recordedVideoDimensionsRef.current) {
        if (video.videoWidth && video.videoHeight) {
          recordedVideoDimensionsRef.current = {
            width: video.videoWidth,
            height: video.videoHeight
          };
          console.log('Captured video dimensions on first frame:', recordedVideoDimensionsRef.current);
        }
      }

      try {
        // Step 0: Sync canvas size to match video
        this.syncCanvasSize();

        // Architecture:
        // - Video element is visible behind canvas overlay
        // - Hidden detection canvas captures video frame for detection
        // - Visible overlay canvas only draws bounding boxes (transparent)
        // This gives maximum performance: no visible canvas video copying

        // Step 1: Draw video to hidden detection canvas
        // Hidden canvas is reused across frames; ensures iOS Safari coordinate consistency
        if (!this._detectionCanvas || this._detectionCanvas.width !== canvas.width || this._detectionCanvas.height !== canvas.height) {
          this._detectionCanvas = document.createElement('canvas');
          this._detectionCanvas.width = canvas.width;
          this._detectionCanvas.height = canvas.height;
          this._detectionCtx = this._detectionCanvas.getContext('2d');
          console.log(`[STRATEGY DEBUG] Created detection canvas: ${canvas.width}x${canvas.height}`);
        }
        this._detectionCtx.drawImage(video, 0, 0);

        // Run detection on single frame
        const inferenceStart = performance.now();
        const rawDetections = await this.runDetection(this._detectionCanvas);
        this._lastInferenceMs = performance.now() - inferenceStart;

        // Step 2: Clear visible overlay canvas (transparent so video shows through)
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Step 3: Compute timestamp (strategy-specific - RAF uses real time, setInterval uses synthetic)
        const timestamp = this.computeTimestamp(frameCounterRef, sessionStartTimeRef);
        const currentFrameIndex = frameCounterRef.current;
        frameCounterRef.current++;

        // Step 4: Convert to frame data format (strategy-specific)
        const frameData = this.buildFrameData(rawDetections, timestamp);

        // Step 5: Store to movement log FIRST (single source of truth)
        // Counter reads from this, ensuring live and replay are identical
        if (currentFrameIndex < this.getMaxRecordingFrames()) {
          movementLogRef.current[currentFrameIndex] = frameData;
        }

        if (onFrameRecorded && frameData) {
          onFrameRecorded(frameData, timestamp, currentFrameIndex);
        }

        // Step 6: Update status (strategy-specific extraction)
        if (onStatusUpdate && frameData) {
          const status = this.getStatusFromFrameData(frameData);
          if (status) {
            onStatusUpdate(status);
          }
        }

        // Step 7: Counter reads FROM movement log (same path as replay)
        const videoDimensions = video.videoWidth && video.videoHeight
          ? { width: video.videoWidth, height: video.videoHeight }
          : null;
        const newRepCount = this.updateCounter(frameData, videoDimensions, timestamp);
        if (newRepCount > strategyCounterTotal) {
          strategyCounterTotal = newRepCount;
          if (onRepDetected) {
            onRepDetected();
          }
        }

        // Step 7.5: Calculate FPS every 10 frames (10, 20, 30, ...)
        if (currentFrameIndex > 0 && currentFrameIndex % 10 === 0) {
          const now = performance.now();
          if (this.lastFpsCheckTime !== null) {
            const elapsedMs = now - this.lastFpsCheckTime;
            const framesDelta = currentFrameIndex - this.lastFpsCheckFrame;
            this.currentFps = (framesDelta / elapsedMs) * 1000;
          }
          this.lastFpsCheckTime = now;
          this.lastFpsCheckFrame = currentFrameIndex;
        }

        // Step 8: Update debug state
        const counterState = this.getCounterState();
        if (counterState && onDebugUpdate) {
          onDebugUpdate(this.buildDebugState(counterState));
        }

        // Step 9: Draw frame overlays (strategy-specific)
        // Note: Video is visible through transparent canvas, only overlays are drawn here
        this.drawFrame(ctx, video, rawDetections, frameData, lastRepCountRef.current);

        // Step 10: Draw FPS and inference time on canvas (if calculated)
        // Canvas is CSS-mirrored with scaleX(-1), so we counter-transform text
        if (this.currentFps !== null) {
          ctx.save();
          ctx.fillStyle = '#ffff00';
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 2;
          ctx.font = 'bold 20px Arial';
          const inferenceMs = this._lastInferenceMs ? this._lastInferenceMs.toFixed(1) : '?';
          const fpsText = `FPS: ${this.currentFps.toFixed(1)} | ${inferenceMs}ms/IF`;
          const textWidth = ctx.measureText(fpsText).width;

          // Draw in bottom-left (appears as bottom-right after CSS mirror)
          const x = 15;
          const y = canvas.height - 15;
          ctx.translate(x + textWidth / 2, y);
          ctx.scale(-1, 1);
          ctx.strokeText(fpsText, -textWidth / 2, 0);
          ctx.fillText(fpsText, -textWidth / 2, 0);
          ctx.restore();
        }

      } catch (err) {
        console.error('Detection error:', err);
      }
    };

    // Create the loop mechanism (RAF or setInterval) - strategy-specific
    return this.createDetectionLoopMechanism(runDetectionCycle, isActiveRef, maxFPS);
  }

  /**
   * Create the detection loop mechanism using requestAnimationFrame
   *
   * Uses rAF for smooth timing with frame skipping for rate limiting:
   * - Target FPS: getRecordingFPS() (default 15 FPS = 66.67ms between frames)
   * - If GPU is fast: skips frames to maintain target FPS
   * - If GPU is slow: runs every frame (natural throttling by inference time)
   * - maxFPS=true: No frame skipping, run as fast as possible
   *
   * @param {Function} runFn - The detection cycle function to run (async)
   * @param {Object} isActiveRef - Ref to check if still active
   * @param {boolean} maxFPS - If true, disable frame skipping for benchmarking
   * @returns {Function} Cleanup function
   */
  createDetectionLoopMechanism(runFn, isActiveRef, maxFPS = false) {
    const targetFPS = this.getRecordingFPS();
    let stopped = false;
    let rafId = null;
    let lastRunTime = 0;
    let isRunning = false; // Prevent overlapping inference calls

    const loop = async (timestamp) => {
      if (stopped || !isActiveRef.current) return;

      // Schedule next frame immediately (keeps rAF loop alive)
      rafId = requestAnimationFrame(loop);

      // Skip if previous inference still running (prevents overlap)
      if (isRunning) return;

      // Use shared frame scheduling logic (testable, handles all Hz)
      const { shouldRun } = shouldProcessFrame(timestamp, lastRunTime, targetFPS, maxFPS);
      if (!shouldRun) return;

      // Run inference
      isRunning = true;
      lastRunTime = timestamp;

      try {
        await runFn();
      } finally {
        isRunning = false;
      }
    };

    // Start the loop
    rafId = requestAnimationFrame(loop);

    return () => {
      stopped = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
  }

  /**
   * Run detection on the video frame
   * @param {HTMLVideoElement} video - Video element
   * @returns {Promise<any>} Raw detections
   */
  runDetection(video) {
    throw new Error('Strategy must implement runDetection');
  }

  /**
   * Build frame data from raw detections
   * @param {any} rawDetections - Raw detections from runDetection
   * @param {number} timestamp - Frame timestamp
   * @returns {Object} Frame data object to store in movement log
   */
  buildFrameData(rawDetections, timestamp) {
    throw new Error('Strategy must implement buildFrameData');
  }

  /**
   * Compute timestamp for the current frame
   * @param {Object} frameCounterRef - Ref to frame counter
   * @param {Object} sessionStartTimeRef - Ref to session start time
   * @returns {number} Timestamp in milliseconds
   */
  computeTimestamp(frameCounterRef, sessionStartTimeRef) {
    // Default: synthetic timestamp based on frame count and FPS
    return frameCounterRef.current * (1000 / this.getRecordingFPS());
  }

  /**
   * Extract status object from frame data
   * @param {Object} frameData - Frame data object
   * @returns {Object|null} Status object for onStatusUpdate callback
   */
  getStatusFromFrameData(frameData) {
    return null;
  }

  /**
   * Build debug state from counter state
   * @param {Object} counterState - Counter state object
   * @returns {Object} Debug state object for onDebugUpdate callback
   */
  buildDebugState(counterState) {
    return {};
  }

  /**
   * Draw frame with detections on canvas
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {HTMLVideoElement} video - Video element
   * @param {any} rawDetections - Raw detections
   * @param {Object} frameData - Frame data object
   * @param {number} currentRepCount - Current rep count for display
   */
  drawFrame(ctx, video, rawDetections, frameData, currentRepCount) {
    // Default: no additional drawing
  }

  /**
   * Draw text that remains readable on a CSS-mirrored canvas.
   * Use this for text that should appear in a fixed screen position (like rep count).
   * For text that should follow mirrored objects (like detection labels), use regular fillText.
   *
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {string} text - Text to draw
   * @param {number} x - X position (in screen coordinates, left side of canvas)
   * @param {number} y - Y position
   * @param {Object} options - Drawing options
   * @param {string} options.fillStyle - Fill color
   * @param {string} options.strokeStyle - Stroke color (optional)
   * @param {number} options.lineWidth - Stroke width (optional)
   * @param {string} options.font - Font specification
   */
  drawReadableText(ctx, text, x, y, options = {}) {
    ctx.save();
    if (options.font) ctx.font = options.font;
    if (options.fillStyle) ctx.fillStyle = options.fillStyle;
    if (options.strokeStyle) ctx.strokeStyle = options.strokeStyle;
    if (options.lineWidth) ctx.lineWidth = options.lineWidth;

    const textWidth = ctx.measureText(text).width;

    // Counter-transform: translate to position, flip horizontally
    ctx.translate(x + textWidth / 2, y);
    ctx.scale(-1, 1);

    if (options.strokeStyle) {
      ctx.strokeText(text, -textWidth / 2, 0);
    }
    ctx.fillText(text, -textWidth / 2, 0);
    ctx.restore();
  }

  /**
   * Convert raw detections to frame data format for recording
   * @param {any} detections - Raw detections from detector
   * @param {number} timestamp - Frame timestamp
   * @returns {Object} Frame data object
   */
  convertToFrameData(detections, timestamp) {
    throw new Error('Strategy must implement convertToFrameData');
  }

  /**
   * Render status display (React element or null)
   * @param {Object} status - Detection status object
   * @param {Object} labels - Labels object
   * @returns {React.ReactElement|null}
   */
  renderStatusDisplay(status, labels) {
    return null;
  }

  /**
   * Render debug panel (React element or null)
   * @param {Object} debugState - Debug state object
   * @returns {React.ReactElement|null}
   */
  renderDebugPanel(debugState) {
    return null;
  }

  /**
   * Get initial status state
   * @returns {Object}
   */
  getInitialStatus() {
    return {};
  }

  /**
   * Get initial debug state
   * @returns {Object}
   */
  getInitialDebugState() {
    return {};
  }

  /**
   * Whether this strategy needs recording controls
   * @returns {boolean}
   */
  supportsRecording() {
    return false;
  }

  /**
   * Get maximum recording frames
   * @returns {number}
   */
  getMaxRecordingFrames() {
    return 5000;
  }

  /**
   * Get recording FPS
   * @returns {number}
   */
  getRecordingFPS() {
    return 15;
  }

  /**
   * Initialize movement log array (pre-allocated for YOLO, empty for pose)
   * @param {number} maxFrames - Maximum frames to record
   * @returns {Array}
   */
  initializeMovementLog(maxFrames) {
    return [];
  }

  /**
   * Slice movement log to actual recorded frames
   * @param {Array} log - Movement log array
   * @param {number} frameCount - Number of frames recorded
   * @returns {Array}
   */
  sliceMovementLog(log, frameCount) {
    return log.slice(0, frameCount || log.length);
  }
}
